
import { Box, Typography, Container } from "@mui/material";
import { projectData } from "./ProjectData";
import { motion } from "framer-motion";
import { useNavigation } from "../../hooks/useNavigation";

interface ProjectsProps {
    shownTitles?: string[];
}

export default function ProjectGrid({shownTitles}: ProjectsProps) {
    const { goWorkProjects } = useNavigation();
    
    const handleToSingleProject = (title: string) => {
        goWorkProjects(title);
    }

    const gridDisplay = shownTitles
        ? projectData.filter(project => shownTitles.includes(project.title))
        : projectData;

    return (
        <>
          <Box
                component="section"
                sx={{
                    width: '100%',
                    maxWidth: {
                        xs: '100%',
                        sm: '90%',
                        md: '730px',
                        lg: '1024px',
                        xl: '1270px',
                    },
                    px: { xs: 1, sm: 3, md: 0 },
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',          
                        sm: '1fr',          
                        md: 'repeat(2, 1fr)',
                    },
                    gridAutoRows: {
                        xs: 'minmax(240px, auto)', 
                        sm: '320px',
                        md: '240px',
                        lg: '448px',
                    },
                    rowGap: { xs: 3, md: 4 },
                    columnGap: { xs: 0, md: 4 }, 
                    margin: '0 auto',
                }}
            > 
                {gridDisplay.map((project) => (
                    <motion.div
                        key={project.id}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                        variants={{
                            hidden: { opacity: 0, y: 40 },
                            visible: { opacity: 1, y: 0 },
                        }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        <Box
                            onClick={() => handleToSingleProject(project.title)}
                            tabIndex={0} 
                            role="button"
                            aria-label={`Open project: ${project.title}`}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleToSingleProject(project.title);
                                }
                            }}
                            sx={{
                                width: '100%',
                                maxWidth: {
                                        sm: '100%',
                                        md: '353px',
                                        lg: '585px',
                                    },
                                position: 'relative',
                                height: '100%',
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover .overlay-bg': {
                                    opacity: 1,
                                },
                                '&:hover .image': {
                                    transform: 'scale(1.08)',
                                },
                                '&:hover .overlay': {
                                    opacity: 1,
                                    transform: 'translateY(0)',
                                },
                                '&:hover .arrow-icon': {
                                    transform: 'rotate(0deg)',
                                },
                                '&:hover .arrow-container': {
                                    transform: 'translateX(0)',
                                    opacity: 1,
                                },
                            }}
                            className="tab-indictor"
                        >
                            <Box
                                className="image"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${project.image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                    zIndex: 1,
                                }}
                            />

                            <Box
                                className="overlay-bg"
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    transition: 'opacity 0.4s ease',
                                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                    zIndex: 2,
                                }}
                            />

                            <Box
                                className="overlay"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    height: 'auto',
                                    color: 'white',
                                    opacity: 0,
                                    transform: 'translateY(20px)',
                                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                    zIndex: 3,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '28px 32px',
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography
                                        sx={{ 
                                            fontSize: {
                                                xs: 'clamp(14px, 4vw, 16px)',
                                                sm: 'clamp(16px, 2.5vw, 18px)',
                                                md: 'clamp(18px, 2vw, 20px)'
                                            },
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {project.title}
                                    </Typography>
                                </Box>

                                <Box
                                    className="arrow-container"
                                    sx={{
                                        width: "60px",
                                        height: "60px",
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        flexGrow: 0,
                                        transform: 'translateX(-40px)',
                                        opacity: 0,
                                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                >
                                    <Box
                                        className="arrow-icon"
                                        sx={{
                                            width: '28px',
                                            height: '28px',
                                            transition: 'transform 0.4s ease',
                                            transform: 'rotate(-90deg)',
                                        }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 256 256"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'inline-block',
                                                fill: 'currentColor',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <path d="M200,64V168a8,8,0,0,1-16,0V83.31L69.66,197.66a8,8,0,0,1-11.32-11.32L172.69,72H88a8,8,0,0,1,0-16H192A8,8,0,0,1,200,64Z" />
                                        </svg>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </motion.div>
                ))}
            </Box>
        </>
    );
}